export const MAX_RESTRICTED_SQL_MEASURES = 25;
export const MAX_RESTRICTED_SQL_GROUPING_PARAMETERS = 10;
export const MAX_TABLE_GROUPING_PARAMETERS = 25;
export const getGroupingParameterLimit = (viz = {}) =>
  (viz.visualizationType || "table") === "table" && !viz.variant && !viz.pivotConfig && !viz.timeSeries && !viz.periodComparison
    ? MAX_TABLE_GROUPING_PARAMETERS : MAX_RESTRICTED_SQL_GROUPING_PARAMETERS;
export const MAX_RESTRICTED_SQL_BYTES = 4000;

export const RESTRICTED_SQL_MEASURE_FORMATS = [
  { value: "integer", label: "Integer" },
  { value: "decimal", label: "Decimal" },
  { value: "percentage", label: "Percentage" },
];

// Continue accepting and rendering formats that may already exist in saved reports.
const SUPPORTED_RESTRICTED_SQL_MEASURE_FORMATS = new Set([
  "number",
  "integer",
  "decimal",
  "percentage",
  "currency",
]);

export const formatRestrictedSqlValue = (value, format = "number") => {
  if (value === null || value === undefined || value === "") return "-";
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return String(value);
  const options = {
    integer: { maximumFractionDigits: 0 },
    decimal: { minimumFractionDigits: 2, maximumFractionDigits: 2 },
    percentage: {
      style: "percent",
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    },
    currency: {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    },
    number: { maximumFractionDigits: 2 },
  };
  return new Intl.NumberFormat("en-IN", options[format] || options.number).format(
    numericValue
  );
};

const FORBIDDEN_SQL_PATTERN =
  /(?:;|--|\/\*|\*\/|\b(?:select|from|join|with|union|intersect|except|group\s+by|order\s+by|limit|offset|having|over|alter|delete|update|insert|drop|truncate|create|grant|revoke|copy|execute|call)\b|::|->|#>|~)/i;

const AGGREGATE_PATTERN = /\b(?:count|sum|avg|min|max)\s*\(/i;

const withoutSqlStringLiterals = (expression) =>
  expression.replace(/'(?:''|[^'])*'/g, "''");

export const normalizeLogicalFieldName = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .replace(/_+/g, "_");

export const createRestrictedSqlMeasure = (ordinal = 1, reportLevel = "customers") => {
  const fallbackId = `measure_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  const cryptoApi = typeof window !== "undefined" ? window.crypto : null;
  const generatedId =
    typeof cryptoApi?.randomUUID === "function"
      ? `measure_${cryptoApi.randomUUID().replace(/-/g, "")}`
      : fallbackId;
  return {
    id: generatedId,
    label: ordinal === 1 ? (reportLevel === "calls" ? "Conversations" : "Customers") : `Measure ${ordinal}`,
    sqlExpression: reportLevel === "calls" ? "COUNT(DISTINCT metadata.conversation_id)" : "COUNT(DISTINCT metadata.customer_id)",
    format: "integer",
  };
};

export const hasRestrictedSqlMeasures = (visualization) =>
  Array.isArray(visualization?.measures) && visualization.measures.length > 0;

export const normalizeRestrictedSqlMeasures = (measures = []) =>
  (Array.isArray(measures) ? measures : []).map((measure, index) => ({
    id: String(measure?.id || `measure_${index + 1}`),
    label: String(measure?.label || ""),
    sqlExpression: String(measure?.sqlExpression ?? measure?.sql_expression ?? ""),
    format: String(measure?.format || "integer").toLowerCase(),
  }));

export const reorderRestrictedSqlMeasures = (measures, fromIndex, toIndex) => {
  if (!Array.isArray(measures) || fromIndex === toIndex) return measures;
  if (fromIndex < 0 || toIndex < 0 || fromIndex >= measures.length || toIndex >= measures.length) {
    return measures;
  }
  const next = [...measures];
  const [moved] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, moved);
  return next;
};

export const getUtf8ByteLength = (expression) => {
  if (typeof TextEncoder !== "undefined") return new TextEncoder().encode(expression).length;
  return encodeURIComponent(expression).replace(/%[0-9A-F]{2}|./gi, "x").length;
};

export const validateRestrictedSqlMeasures = (visualization) => {
  const measures = normalizeRestrictedSqlMeasures(visualization?.measures);
  const errors = [];

  const dimensionPairs = Array.isArray(visualization?.orderedParameterPairs)
    ? visualization.orderedParameterPairs
    : [];
  const limit = getGroupingParameterLimit(visualization);
  if (dimensionPairs.length > limit) {
    errors.push({
      field: "parameters",
      message: `This visualization supports at most ${limit} grouping parameters.`,
    });
  }
  if (measures.length === 0) return errors;

  if (measures.length > MAX_RESTRICTED_SQL_MEASURES) {
    errors.push({
      field: "measures",
      message: `A visualization can contain at most ${MAX_RESTRICTED_SQL_MEASURES} SQL measures.`,
    });
  }

  const seenIds = new Set();
  measures.forEach((measure, index) => {
    const location = { measureId: measure.id, measureIndex: index };
    if (!/^[A-Za-z][A-Za-z0-9_-]{0,63}$/.test(measure.id)) {
      errors.push({ ...location, field: "id", message: "Measure ID is invalid." });
    } else if (seenIds.has(measure.id)) {
      errors.push({ ...location, field: "id", message: "Measure IDs must be unique." });
    }
    seenIds.add(measure.id);

    if (!measure.label.trim()) {
      errors.push({ ...location, field: "label", message: "Enter a measure label." });
    } else if (measure.label.trim().length > 100) {
      errors.push({ ...location, field: "label", message: "Measure label must be 100 characters or fewer." });
    } else if (
      /^[=+\-@]/.test(measure.label.trim()) ||
      Array.from(measure.label).some((character) => character.charCodeAt(0) < 32)
    ) {
      errors.push({
        ...location,
        field: "label",
        message: "Measure labels cannot begin with =, +, -, or @ or contain control characters.",
      });
    }

    const expression = measure.sqlExpression.trim();
    if (!expression) {
      errors.push({ ...location, field: "sqlExpression", message: "Enter an aggregate SQL expression." });
    } else {
      if (getUtf8ByteLength(expression) > MAX_RESTRICTED_SQL_BYTES) {
        errors.push({
          ...location,
          field: "sqlExpression",
          message: `SQL expression must be ${MAX_RESTRICTED_SQL_BYTES} bytes or fewer.`,
        });
      }
      if (FORBIDDEN_SQL_PATTERN.test(withoutSqlStringLiterals(expression))) {
        errors.push({
          ...location,
          field: "sqlExpression",
          message: "Only an aggregate expression is allowed. Full SQL statements and unsafe operators are not allowed.",
        });
      }
      if (!AGGREGATE_PATTERN.test(expression)) {
        errors.push({
          ...location,
          field: "sqlExpression",
          message: "The expression must contain COUNT, SUM, AVG, MIN, or MAX.",
        });
      }
    }

    if (!SUPPORTED_RESTRICTED_SQL_MEASURE_FORMATS.has(measure.format)) {
      errors.push({ ...location, field: "format", message: "Select a supported number format." });
    }
  });

  const effectiveType =
    visualization?.visualizationType === "table" && visualization?.variant === "pivot_table"
      ? "pivot_table"
      : visualization?.visualizationType;
  if (["pie_chart", "donut_chart"].includes(effectiveType)) {
    const ids = new Set(measures.map((measure) => measure.id));
    if (!visualization?.displayMeasureId || !ids.has(String(visualization.displayMeasureId))) {
      errors.push({
        field: "displayMeasureId",
        message: "Select which SQL measure the chart should display.",
      });
    }
  }

  return errors;
};

export const buildRestrictedSqlFieldOptions = ({ metadata = [], businessInsights = [], customModules = [], agentFeedback = [] }) => {
  const options = [];
  const seen = new Set();
  const add = (namespace, rawName, label) => {
    const field = normalizeLogicalFieldName(rawName);
    if (!field) return;
    const value = `${namespace}.${field}`;
    if (seen.has(value)) return;
    seen.add(value);
    options.push({ value, label: label || rawName || field, namespace });
  };

  metadata.forEach((item) => add("metadata", item?.value ?? item, item?.label));
  businessInsights.forEach((item) => add("business_insights", item?.value ?? item, item?.label));
  customModules.forEach((item) => add("custom_module", item?.value ?? item, item?.label));
  agentFeedback.forEach((item) => add("agent_feedback", item?.value ?? item, item?.label));
  return options;
};

const toErrorArray = (payload) => {
  if (Array.isArray(payload?.errors)) return payload.errors;
  if (Array.isArray(payload?.details)) return payload.details;
  if (Array.isArray(payload?.error?.details)) return payload.error.details;
  return [];
};

export const extractRestrictedSqlApiErrors = (payload) =>
  toErrorArray(payload).map((error) => {
    const path = Array.isArray(error?.path) ? error.path.join(".") : String(error?.path || "");
    const visualizationPathMatch = path.match(/visualizations?\[?(\d+)\]?/i);
    const measurePathMatch = path.match(/measures?\[?(\d+)\]?/i);
    const fieldPathMatch = path.match(/(?:\.|\])([A-Za-z_][A-Za-z0-9_]*)$/);
    return {
      visualizationId:
        error?.visualizationId ?? error?.visualization_id ?? error?.visualization ?? null,
      visualizationIndex:
        error?.visualizationIndex ??
        error?.visualization_index ??
        (visualizationPathMatch ? Number(visualizationPathMatch[1]) : null),
      measureId: error?.measureId ?? error?.measure_id ?? error?.measure ?? null,
      measureIndex:
        error?.measureIndex ??
        error?.measure_index ??
        (measurePathMatch ? Number(measurePathMatch[1]) : null),
      field: error?.field || fieldPathMatch?.[1] || "sqlExpression",
      message: error?.message || error?.detail || String(error),
    };
  });

export const getRestrictedSqlErrorMessage = (payload, fallback) =>
  payload?.message || payload?.detail || payload?.error?.message || fallback;
